import { useEffect, useMemo, useState } from 'react';
import { CourseModal } from './components/CourseModal';
import { ScheduleGrid } from './components/ScheduleGrid';
import { SettingsPanel } from './components/SettingsPanel';
import { fetchConfig, fetchSchedule, getStoredAdminToken, saveAdjustment, saveConfig, setStoredAdminToken } from './api';
import type { CourseItem, PublicConfig } from './shared/types';
import {
  addDays,
  getWeekDates,
  todayYmd,
  tomorrowYmd,
  weekStartMonday,
  formatRelativeWeek,
  formatDateLabel,
} from './shared/time';

const DEFAULT_CONFIG: PublicConfig = {
  mail_enabled: false,
  mail_time: '22:00',
  mail_to: '',
  mail_provider: 'resend',
  mail_from: '',
  last_mail_sent_ymd: '',
  has_admin_password: false,
};

type CourseMap = Record<string, CourseItem[]>;

export default function App() {
  const today = todayYmd();
  const tomorrow = tomorrowYmd();
  const [anchorWeekStart, setAnchorWeekStart] = useState(() => weekStartMonday(today));
  const [courseMap, setCourseMap] = useState<CourseMap>({});
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [adminToken, setAdminToken] = useState(() => getStoredAdminToken());
  const [tokenInput, setTokenInput] = useState(() => getStoredAdminToken());
  const [config, setConfig] = useState<PublicConfig>(DEFAULT_CONFIG);
  const [configBusy, setConfigBusy] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<CourseItem | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  const weekDates = useMemo(() => getWeekDates(anchorWeekStart), [anchorWeekStart]);

  const weekLabel = useMemo(() => {
    const rel = formatRelativeWeek(anchorWeekStart, weekStartMonday(today));
    return `${rel}  ${formatDateLabel(anchorWeekStart)} — ${formatDateLabel(addDays(anchorWeekStart, 6))}`;
  }, [anchorWeekStart, today]);

  async function loadWeek() {
    setLoadingWeek(true);
    try {
      const entries = await Promise.all(
        weekDates.map(async (date) => {
          const schedule = await fetchSchedule(date);
          return [date, schedule] as const;
        }),
      );
      setCourseMap(Object.fromEntries(entries));
    } catch (err) {
      setStatusMsg(err instanceof Error ? err.message : '课表加载失败');
    } finally {
      setLoadingWeek(false);
    }
  }

  async function loadConfig() {
    if (!adminToken) return;
    try {
      const remote = await fetchConfig();
      setConfig(remote);
    } catch (err) {
      setStatusMsg(err instanceof Error ? err.message : '配置读取失败');
    }
  }

  useEffect(() => { void loadWeek(); }, [anchorWeekStart]);
  useEffect(() => { void loadConfig(); }, [adminToken]);

  async function handleAdjustmentSubmit(payload: Record<string, unknown>) {
    await saveAdjustment(payload);
    await loadWeek();
    setSelectedCourse(null);
  }

  async function handleSaveConfig() {
    setConfigBusy(true);
    try {
      await saveConfig({
        mail_enabled: config.mail_enabled,
        mail_time: config.mail_time,
        mail_to: config.mail_to,
        mail_provider: config.mail_provider,
        mail_from: config.mail_from,
      });
      await loadConfig();
      setStatusMsg('配置已保存');
    } catch (err) {
      setStatusMsg(err instanceof Error ? err.message : '保存失败');
    } finally {
      setConfigBusy(false);
    }
  }

  function applyToken() {
    const t = tokenInput.trim();
    setStoredAdminToken(t);
    setAdminToken(t);
    setStatusMsg(t ? '已登录' : '已清除 Token');
  }

  const todayCourses = courseMap[today] ?? [];
  const tomorrowCourses = courseMap[tomorrow] ?? [];
  const isThisWeek = anchorWeekStart === weekStartMonday(today);

  return (
    <div className="min-h-screen bg-[#f5f6f8] text-slate-800">
      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6">

        {/* 顶栏 */}
        <header className="mb-5 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">课程表</h1>
            <p className="mt-0.5 text-sm text-slate-400">{loadingWeek ? '同步中…' : weekLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-500">
              今日 <strong className="text-slate-800">{todayCourses.length}</strong>
              <span className="text-slate-300">·</span>
              明日 <strong className="text-slate-800">{tomorrowCourses.length}</strong>
            </span>
            <button
              type="button"
              onClick={() => setAdminOpen((v) => !v)}
              className={[
                'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                adminOpen
                  ? 'border-blue-300 bg-blue-50 text-blue-600'
                  : adminToken
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-600'
                    : 'border-slate-200 bg-white text-slate-500 hover:text-slate-700',
              ].join(' ')}
            >
              {adminToken ? '管理员' : '登录'}
            </button>
          </div>
        </header>

        {/* 管理面板（折叠） */}
        {adminOpen && (
          <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="mb-1.5 block text-xs text-slate-500">管理员 Token</label>
                <input
                  type="password"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyToken()}
                  placeholder="输入密码后回车"
                  className="h-9 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-blue-400 focus:bg-white"
                />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={applyToken}
                  className="h-9 rounded-xl bg-blue-500 px-4 text-sm font-medium text-white hover:bg-blue-600 transition">
                  确认
                </button>
                <button type="button" onClick={() => { void loadConfig(); setStatusMsg('已刷新'); }}
                  className="h-9 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-600 hover:bg-slate-100 transition">
                  刷新
                </button>
              </div>
            </div>
            {statusMsg && <p className="mt-2 text-xs text-slate-400">{statusMsg}</p>}

            {adminToken && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <SettingsPanel config={config} busy={configBusy} onChange={setConfig} onSave={handleSaveConfig} />
              </div>
            )}
          </div>
        )}

        {/* 今日 + 明日置顶卡片 */}
        <div className="mb-5 grid gap-3 sm:grid-cols-2">
          <DayCard
            label="今天"
            date={today}
            courses={todayCourses}
            accent="today"
            onCourseClick={(course) => {
              if (!adminToken) { setAdminOpen(true); return; }
              setSelectedCourse(course);
            }}
          />
          <DayCard
            label="明天"
            date={tomorrow}
            courses={tomorrowCourses}
            accent="tomorrow"
            onCourseClick={(course) => {
              if (!adminToken) { setAdminOpen(true); return; }
              setSelectedCourse(course);
            }}
          />
        </div>

        {/* 周导航 */}
        <div className="mb-3 flex items-center gap-2">
          <span className="text-xs font-medium text-slate-400 mr-1">本周课表</span>
          <button type="button" onClick={() => setAnchorWeekStart(addDays(anchorWeekStart, -7))}
            className="h-7 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-500 hover:bg-slate-50 transition">
            ‹ 上周
          </button>
          {!isThisWeek && (
            <button type="button" onClick={() => setAnchorWeekStart(weekStartMonday(today))}
              className="h-7 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-xs text-blue-500 hover:bg-blue-100 transition">
              回本周
            </button>
          )}
          <button type="button" onClick={() => setAnchorWeekStart(addDays(anchorWeekStart, 7))}
            className="h-7 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-500 hover:bg-slate-50 transition">
            下周 ›
          </button>
        </div>

        {/* 周视图网格 */}
        <ScheduleGrid
          dates={weekDates}
          courseMap={courseMap}
          today={today}
          tomorrow={tomorrow}
          onCourseClick={(course) => {
            if (!adminToken) { setAdminOpen(true); return; }
            setSelectedCourse(course);
          }}
        />
      </div>

      {selectedCourse && (
        <CourseModal
          course={selectedCourse}
          defaultDate={selectedCourse.date}
          onClose={() => setSelectedCourse(null)}
          onSubmit={handleAdjustmentSubmit}
        />
      )}
    </div>
  );
}

// ── 今日/明日置顶卡片 ──────────────────────────────────────────
import type { CourseItem as CI } from './shared/types';
import { weekdayLabel } from './shared/time';

const DAY_SOURCE_COLOR: Record<string, string> = {
  baseline: 'text-slate-400',
  override: 'text-indigo-400',
  'temporary-modify': 'text-amber-500',
  'temporary-add': 'text-emerald-500',
};
const DAY_SOURCE_LABEL: Record<string, string> = {
  baseline: '基线', override: '永久改', 'temporary-modify': '临时改', 'temporary-add': '临时加',
};

function DayCard({
  label, date, courses, accent, onCourseClick,
}: {
  label: string; date: string; courses: CI[]; accent: 'today' | 'tomorrow';
  onCourseClick: (c: CI) => void;
}) {
  const isToday = accent === 'today';
  return (
    <div className={[
      'rounded-2xl border p-4',
      isToday ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50',
    ].join(' ')}>
      <div className="mb-3 flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <span className={['text-sm font-semibold', isToday ? 'text-blue-600' : 'text-amber-600'].join(' ')}>
            {label}
          </span>
          <span className="text-xs text-slate-400">{weekdayLabel(date)} · {date}</span>
        </div>
        <span className="text-xs text-slate-400">{courses.length} 节</span>
      </div>
      <div className="flex flex-col gap-2">
        {courses.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">
            无课
          </div>
        ) : courses.map((course) => (
          <button
            key={course.uid}
            type="button"
            onClick={() => onCourseClick(course)}
            className="group w-full rounded-xl border border-white bg-white p-3 text-left shadow-sm transition hover:shadow-md active:scale-[0.99]"
          >
            <div className="flex items-start justify-between gap-1">
              <span className="text-sm font-medium text-slate-800 group-hover:text-blue-600 transition-colors leading-tight">
                {course.summary}
              </span>
              <span className={['shrink-0 text-[10px] mt-0.5', DAY_SOURCE_COLOR[course.source] ?? 'text-slate-400'].join(' ')}>
                {DAY_SOURCE_LABEL[course.source] ?? course.source}
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{course.timeLabel}</div>
            {course.location && <div className="mt-0.5 text-xs text-slate-400 truncate">{course.location}</div>}
            {course.teacher && <div className="mt-0.5 text-xs text-slate-400 truncate">{course.teacher}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}
